import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FiX,
  FiFileText,
  FiDollarSign,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiXCircle,
  FiInfo,
  FiUser,
  FiPrinter,
  FiPlusCircle
} from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import { useImpresion } from '../../contexts/ContextoImpresion';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import ModalPagoPOS from '../venta/ModalPagoPOS';
import PlantillaModal from '../util/PlantillaModal';
import ModalCrearNotaFactura from '../util/modals/ModalCrearNotaFactura';
import { roundTo2 } from '../../miscelaneo/monetario';

// =====================================================
// Helper: format currency
// =====================================================
function fmt(valor) {
  return roundTo2(parseFloat(valor) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// =====================================================
// Helper: payment status badge
// =====================================================
function BadgeEstadoPago({ estado }) {
  const config = {
    pagada:    { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle, label: 'Pagada' },
    parcial:   { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock, label: 'Parcial' },
    pendiente: { bg: 'bg-red-100', text: 'text-red-800', icon: FiAlertTriangle, label: 'Pendiente' }
  };
  const c = config[estado] || config.pendiente;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${c.bg} ${c.text}`}>
      <Icon size={14} /> {c.label}
    </span>
  );
}

// =====================================================
// Tab: General (purchase header + line items)
// =====================================================
function TabGeneral({ compra, detalles }) {
  if (!compra) return null;
  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InfoCard label="NCF" value={compra.ncf || 'Sin NCF'} />
        <InfoCard label="Proveedor" value={compra.proveedor_nombre || '—'} />
        <InfoCard label="RNC" value={compra.proveedor_rnc || '—'} />
        <InfoCard label="Fecha" value={compra.fecha_hora ? new Date(compra.fecha_hora).toLocaleDateString('es-DO') : '—'} />
        <InfoCard label="Fecha de Vencimiento" value={compra.fecha_vencimiento ? new Date(compra.fecha_vencimiento).toLocaleDateString('es-DO') : '—'} />
        <InfoCard label="Sucursal" value={compra.sucursal_nombre || '—'} />
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TotalCard label="Subtotal" value={fmt(compra.monto)} color="blue" />
        <TotalCard label="ITBIS" value={fmt(compra.impuesto_total)} color="purple" />
        <TotalCard label="Total" value={fmt(compra.monto_total)} color="green" />
        <TotalCard label="Balance" value={fmt(compra.balance)} color={parseFloat(compra.balance) > 0 ? 'red' : 'green'} />
      </div>

      {/* Line items table */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Detalle de Productos</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Producto</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Cant.</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Precio</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">ITBIS</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(detalles || []).map((d, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{d.producto_nombre || '—'}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{d.cantidad}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(d.precio)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(d.impuesto)}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmt(d.subtotal)}</td>
                </tr>
              ))}
              {(!detalles || detalles.length === 0) && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Sin productos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Comment */}
      {compra.comentario && (
        <div className="bg-gray-50 p-3 rounded-lg">
          <span className="text-xs font-medium text-gray-500">Comentario:</span>
          <p className="text-sm text-gray-700 mt-1">{compra.comentario}</p>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{value}</p>
    </div>
  );
}

function TotalCard({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200'
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[color] || colors.blue}`}>
      <span className="text-xs font-medium opacity-75">{label}</span>
      <p className="text-lg font-bold mt-0.5">${value}</p>
    </div>
  );
}

// =====================================================
// SUB-COMPONENT: Modal de Anulación de Pago (CxP)
// =====================================================
function ModalAnulacionCompra({ abierto, onCerrar, pago, onAnulado }) {
  const [razones, setRazones] = useState([]);
  const [formData, setFormData] = useState({ id_razon_anulacion_pago: '', comentario: '' });
  const [anulando, setAnulando] = useState(false);

  useEffect(() => {
    if (abierto) {
      setFormData({ id_razon_anulacion_pago: '', comentario: '' });
      instanciaAxios.get('/pago/razon_anulacion_pago')
        .then(res => setRazones(res.data))
        .catch(() => {
          setRazones([
            { id_razon_anulacion_pago: 1, descripcion: 'Error de digitación' },
            { id_razon_anulacion_pago: 2, descripcion: 'Pago duplicado' },
            { id_razon_anulacion_pago: 3, descripcion: 'Monto incorrecto' },
            { id_razon_anulacion_pago: 4, descripcion: 'Método de pago incorrecto' },
            { id_razon_anulacion_pago: 5, descripcion: 'Otro' }
          ]);
        });
    }
  }, [abierto]);

  const handleAnular = async () => {
    if (!formData.id_razon_anulacion_pago) {
      toast.error('Seleccione una razón de anulación');
      return;
    }
    setAnulando(true);
    try {
      await instanciaAxios.put(`/pago/compra/anular/${pago.id_pago_compra}`, formData);
      toast.success('Pago anulado correctamente');
      onAnulado();
      onCerrar();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error(error.response?.data?.mensaje || 'Error al anular pago');
    } finally {
      setAnulando(false);
    }
  };

  if (!pago) return null;

  return (
    <PlantillaModal mostrar={abierto} onClose={onCerrar} titulo="Anular Pago de Compra">
      <div className="space-y-5">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="text-amber-500 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-800">¿Está seguro de anular este pago?</p>
              <p className="text-sm text-amber-700 mt-1">
                Monto: <span className="font-semibold">RD$ {fmt(pago.monto)}</span>
              </p>
              <p className="text-sm text-amber-700">Método: {pago.metodo_pago_nombre}</p>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Razón de Anulación *</label>
          <select
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            value={formData.id_razon_anulacion_pago}
            onChange={(e) => setFormData(prev => ({ ...prev, id_razon_anulacion_pago: e.target.value }))}
          >
            <option value="">Seleccione una razón...</option>
            {razones.map(r => (
              <option key={r.id_razon_anulacion_pago} value={r.id_razon_anulacion_pago}>{r.descripcion}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Comentario (opcional)</label>
          <textarea
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={3}
            placeholder="Detalles adicionales..."
            value={formData.comentario}
            onChange={(e) => setFormData(prev => ({ ...prev, comentario: e.target.value }))}
          />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onCerrar} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" disabled={anulando}>
            Cancelar
          </button>
          <button onClick={handleAnular} disabled={anulando} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50">
            {anulando ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Anulando...</>
            ) : (
              <><FiXCircle size={18} />Confirmar Anulación</>
            )}
          </button>
        </div>
      </div>
    </PlantillaModal>
  );
}

// =====================================================
// Tab: Pagos (payment list + annul)
// =====================================================
function TabPagos({ pagos, resumen, tienePermisoAnular, onAnularPago }) {
  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
          <span className="text-xs font-medium text-green-600">Total Pagado</span>
          <p className="text-lg font-bold text-green-700">${fmt(resumen?.total_pagado)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <span className="text-xs font-medium text-blue-600">Pagos Registrados</span>
          <p className="text-lg font-bold text-blue-700">{resumen?.cantidad_pagos || 0}</p>
        </div>
        {(resumen?.total_anulado || 0) > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <span className="text-xs font-medium text-gray-600">Total Anulado</span>
            <p className="text-lg font-bold text-gray-500">${fmt(resumen?.total_anulado)}</p>
          </div>
        )}
      </div>

      {/* Payment table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Fecha</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Método</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Cuenta</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Monto</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Usuario</th>
              <th className="text-center px-3 py-2 font-medium text-gray-600">Estado</th>
              <th className="text-center px-3 py-2 font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(pagos || []).map((p, i) => (
              <tr key={i} className={`hover:bg-gray-50 ${p.anulado ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                  {p.fecha_hora ? new Date(p.fecha_hora).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  <div className="flex items-center gap-1">
                    <FiCreditCard size={14} className="text-gray-400" />
                    {p.metodo_pago_nombre || '—'}
                  </div>
                </td>
                <td className="px-3 py-2 text-gray-600 text-xs">{p.cuenta_tesoreria_nombre || '—'}</td>
                <td className={`px-3 py-2 text-right font-medium ${p.anulado ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  ${fmt(p.monto)}
                </td>
                <td className="px-3 py-2 text-gray-600 text-xs">{p.usuario_nombre || '—'}</td>
                <td className="px-3 py-2 text-center">
                  {p.anulado ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      <FiXCircle size={12} /> Anulado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <FiCheckCircle size={12} /> Activo
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {!p.anulado && tienePermisoAnular && (
                    <button
                      onClick={() => onAnularPago(p)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Anular pago"
                    >
                      <FiXCircle size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(!pagos || pagos.length === 0) && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No hay pagos registrados</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================
// Tab: Notas de Crédito
// =====================================================
function TabNotas({ notasCredito, mostrarAyudaAnular, pagosActivos, onAnularParaCrearNota }) {
  return (
    <div className="space-y-4">
      {mostrarAyudaAnular && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-amber-800 font-medium">
            Esta compra ya no tiene balance disponible (fue pagada por completo). Para crear
            la Nota de Crédito, primero anule uno de los pagos:
          </p>
          <div className="flex flex-wrap gap-2">
            {pagosActivos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onAnularParaCrearNota(p)}
                className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
              >
                Anular {p.metodo_pago_nombre || 'pago'} de ${fmt(p.monto)}
                {p.fecha_hora ? ` (${new Date(p.fecha_hora).toLocaleDateString('es-DO')})` : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      {(!notasCredito || notasCredito.length === 0) ? (
        <div className="text-center py-12 text-gray-400">
          <FiInfo size={32} className="mx-auto mb-2 opacity-50" />
          <p>No hay notas de crédito asociadas</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">NCF</th>
                <th className="text-center px-4 py-2 font-medium text-gray-600">Tipo</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Monto</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Fecha</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Comentario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notasCredito.map((n, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-800">{n.ncf || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      n.tipo_nota === 'E34' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {n.tipo_nota === 'E34' ? 'Electrónica (E34)' : 'Local'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">${fmt(n.monto)}</td>
                  <td className="px-4 py-2 text-gray-700">
                    {n.fecha_hora ? new Date(n.fecha_hora).toLocaleDateString('es-DO') : '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-600 text-xs">{n.comentario || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =====================================================
// MAIN: ModalDetalleCompra
// =====================================================
export default function ModalDetalleCompra({ abierto, onCerrar, idCompra, onCompraActualizada }) {
  const { tienePermiso } = useContextoSesion();
  const { imprimirDocumento } = useImpresion();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [tabActivo, setTabActivo] = useState('general');
  const [mostrarPDF, setMostrarPDF] = useState(false);
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  const [pagoAnular, setPagoAnular] = useState(null);
  const [modalAnulacionAbierto, setModalAnulacionAbierto] = useState(false);
  const [mostrarModalNC, setMostrarModalNC] = useState(false);
  // Cuando el usuario elige "anular pago para crear NC" desde la tab Notas,
  // este flag hace que, al confirmar la anulación, se abra automáticamente
  // el modal de Nota de Crédito en vez de solo recargar los datos.
  const [flujoNotaTrasAnular, setFlujoNotaTrasAnular] = useState(false);

  // Load purchase data
  const cargarCompra = useCallback(async () => {
    if (!idCompra) return;
    setCargando(true);
    try {
      const resp = await instanciaAxios.get(`/cxp/compra/${idCompra}`);
      setData(resp.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar compra:', error);
      toast.error('Error al cargar los detalles de la compra');
    } finally {
      setCargando(false);
    }
  }, [idCompra]);

  useEffect(() => {
    if (abierto && idCompra) {
      setTabActivo('general');
      setData(null);
      cargarCompra();
    }
  }, [abierto, idCompra, cargarCompra]);

  const handlePagoRegistrado = () => {
    setMostrarModalPago(false);
    cargarCompra();
    if (onCompraActualizada) onCompraActualizada();
  };

  if (!abierto) return null;

  const compra = data?.compra;
  const balance = parseFloat(compra?.balance) || 0;

  // Puede recibir una Nota de Crédito electrónica (E34) solo si el E41 ya fue
  // enviado/aceptado por DGII (tiene_e41 + dgii_track_id).
  const condicionesBaseE41 = !!(compra?.es_informal && compra?.tiene_e41 && compra?.dgii_track_id);
  const puedeCrearNotaCreditoE41 = condicionesBaseE41 && balance > 0;
  const pagosActivos = (data?.pagos || []).filter(p => !p.anulado);
  // Compra E41 totalmente pagada: en vez de dejar la NC inalcanzable, se
  // ofrece anular un pago y continuar directo a crear la NC.
  const necesitaAnularParaNC = condicionesBaseE41 && balance <= 0 && pagosActivos.length > 0
    && tienePermiso('compra_nota_credito') && tienePermiso('compra_anular_pago');

  // Adapta la compra (+ detalles) a la forma que espera ModalCrearNotaFactura,
  // reutilizando el mismo modal que se usa para NC de facturas de venta.
  const compraParaNotaCredito = compra ? {
    id_compra: compra.id_compra,
    ncf: compra.ncf,
    monto_total: compra.monto_total,
    balance: compra.balance,
    nombre_cliente: compra.proveedor_nombre,
    detalles_factura: (data?.detalles || []).map((d, index) => ({
      id_detalle_factura: d.id_producto || index,
      id_producto: d.id_producto,
      descripcion: d.producto_nombre,
      cantidad: d.cantidad,
      precio: d.precio,
      itbis_tasa: d.itbis_tasa,
      indicador_facturacion: d.indicador_facturacion || 1,
      descuento: d.descuento
    }))
  } : null;

  const tabs = [
    { id: 'general', label: 'General', icon: FiFileText },
    { id: 'pagos', label: 'Pagos', icon: FiDollarSign, badge: data?.resumen_pagos?.cantidad_pagos },
    { id: 'notas', label: 'Notas', icon: FiAlertTriangle, badge: data?.notas_credito?.length || 0 },
    { id: 'pdf', label: 'PDF', icon: FiPrinter }
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onCerrar} />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-12 z-50 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {compra?.ncf || 'Cargando...'}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                {compra && <BadgeEstadoPago estado={compra.estado_pago} />}
                {compra?.proveedor_nombre && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <FiUser size={12} /> {compra.proveedor_nombre}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {balance > 0 && compra && tienePermiso('compra_pago') && (
              <button
                onClick={() => setMostrarModalPago(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <FiDollarSign size={16} />
                Registrar Pago
              </button>
            )}
            {puedeCrearNotaCreditoE41 && tienePermiso('compra_nota_credito') && (
              <button
                onClick={() => setMostrarModalNC(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                title="Crear Nota de Crédito electrónica (E34) sobre este E41"
              >
                <FiPlusCircle size={16} />
                Crear Nota de Crédito
              </button>
            )}
            <button
              onClick={onCerrar}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = tabActivo === tab.id;
            return (
              <button
                key={tab.id}
                onClick={async () => {
                  if (tab.id === 'pdf') {
                    const metodo = await imprimirDocumento({ tipoDocumento: 'compra', idDocumento: idCompra });
                    if (!metodo) {
                      setMostrarPDF(true);
                    }
                  } else {
                    setTabActivo(tab.id);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {cargando ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerCargando />
            </div>
          ) : !data ? (
            <div className="text-center py-20 text-gray-400">
              <FiInfo size={40} className="mx-auto mb-3 opacity-50" />
              <p>No se pudieron cargar los datos de la compra</p>
            </div>
          ) : (
            <>
              {tabActivo === 'general' && <TabGeneral compra={compra} detalles={data.detalles} />}
              {tabActivo === 'pagos' && (
                <TabPagos
                  pagos={data.pagos}
                  resumen={data.resumen_pagos}
                  tienePermisoAnular={tienePermiso('compra_anular_pago')}
                  onAnularPago={(pago) => {
                    setPagoAnular(pago);
                    setModalAnulacionAbierto(true);
                  }}
                />
              )}
              {tabActivo === 'notas' && (
                <TabNotas
                  notasCredito={data.notas_credito}
                  mostrarAyudaAnular={necesitaAnularParaNC}
                  pagosActivos={pagosActivos}
                  onAnularParaCrearNota={(pago) => {
                    setPagoAnular(pago);
                    setFlujoNotaTrasAnular(true);
                    setModalAnulacionAbierto(true);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Payment Modal (CxP mode) */}
      {mostrarModalPago && compra && (
        <ModalPagoPOS
          mostrar={mostrarModalPago}
          onClose={() => setMostrarModalPago(false)}
          ticket={{
            totales: {
              total: parseFloat(compra.balance) || 0,
              subtotal: parseFloat(compra.monto) || 0,
              itbis: parseFloat(compra.impuesto_total) || 0
            }
          }}
          onConfirmarPago={handlePagoRegistrado}
          formatoMoneda={(v) => `$${fmt(v)}`}
          modo="cxp"
          facturaExistente={{
            id_compra: compra.id_compra,
            id_entidad_proveedor: compra.id_entidad_proveedor,
            balance: parseFloat(compra.balance) || 0,
            monto_total: parseFloat(compra.monto_total) || 0
          }}
        />
      )}

      {/* PDF Modal */}
      <ModalPDFBase64
        mostrar={mostrarPDF}
        onClose={() => setMostrarPDF(false)}
        idCompra={idCompra}
        ncf={compra?.ncf}
        tituloDocumento="Compra"
        tipoDocumento="compra"
      />

      {/* Annulment Modal */}
      <ModalAnulacionCompra
        abierto={modalAnulacionAbierto}
        onCerrar={() => {
          setModalAnulacionAbierto(false);
          setPagoAnular(null);
          setFlujoNotaTrasAnular(false);
        }}
        pago={pagoAnular}
        onAnulado={async () => {
          await cargarCompra();
          onCompraActualizada && onCompraActualizada();
          if (flujoNotaTrasAnular) {
            setFlujoNotaTrasAnular(false);
            setMostrarModalNC(true);
          }
        }}
      />

      {/* Nota de Crédito (E34) sobre compra E41 */}
      {mostrarModalNC && compraParaNotaCredito && (
        <ModalCrearNotaFactura
          mostrar={mostrarModalNC}
          onClose={() => setMostrarModalNC(false)}
          facturaOriginal={compraParaNotaCredito}
          tipoNota="credito"
          tipoOrigen="compra"
          onExito={() => {
            cargarCompra();
            onCompraActualizada && onCompraActualizada();
          }}
        />
      )}
    </>
  );
}

